/* prompt
 *
 *
You are an experienced Scala programmer using functional programming, Cats and Either[Error, T] to return values.
Use Scala.js to write HTML interactive tree explorer component using pure DOM + CSS.
Use Scala 3 syntax (take care for lambda parameter in parentheses).

The tree consists of nodes coming from the backend.

   case class TreeNode(
  	key: String,
  	parentKey: Option[String],
  	caption: String,
  	icon: String,
  	toolTip: String,
  	properties: Map[Int, String],
  	children: Option[Seq[TreeNode]],
  ) {
	def isLeaf: Boolean = children match {
  	case None => false
  	case Some(children) => children.isEmpty
	}
  }

Each node has a string key and optionally parent key, if it is not a root node. 
Each node has a caption, icon and tooltip text.
Each node has properties, feel free to ignore them.


The nodes are loaded dynamically.
Each node has an optional list of children.
If it is None, the child nodes have not been loaded yet.
If it is Some(list), the child nodes have already been loaded. If the list is empty, the node is a leaf, it doesn't have any children. 
Loaded children remain persistent in the tree, so there is no need to load it again.

When creating the tree, a callback function is passed as a parameter:

  loadChildrenFn: String => Future[Seq[TreeNode]]

This function is lazily called to get children nodes for a given key. Newly loaded nodes extend the currently loaded part of the tree.


One node at any moment is always selected. At the beginning, after creating the tree, the root node is selected. After loadChildrenFunction Future is finished, a node with the key which the children has been loaded for is selected.

When the user clicks on the node with a mouse, this node becomes selected. When the user clicks the plus sign, the node expands. When the user clicks the minus sign, the node collapses.

When node is selected, only its root element is highlighted, not its children.

The node is either expanded or collapsed, or it is a leaf. Expanded node has a minus sign to collapse it. Collapsed node has a plus sign to expand it. 

Plus and minus signs have black border.

If the user presses the right key on a collapsed node, it will be expanded.
If the user presses the left key on an expanded node, it will collapse. 
If the user presses the left key on a collapsed node, its parent is selected.
If the user presses the right key on an expanded node, its first child is selected.

If the user presses the up arrow key and its previous sibling is collapsed, select the sibling
If the user presses the up arrow key ant its previous sibling is expanded,
select its last child or last grandchild etc recursively.

If the user presses the down arrow key and the node is expanded, select its first child.
If the user presses the down arrow key, the node is collapsed and has next sibling, select its next sibling.
If the user presses the down arrow key, the node is collapsed and is the last child, select select next sibling of the closest expanded parent.

For the user actions, another callback function is entered as a parameter.

val userActionFn: TreeViewUserAction => Future[Unit] 

where:
  trait TreeViewUserAction
  case class NodeSelected(node: TreeNode)
  case class NodeOpened(node: TreeNode, ctrlKey: Boolean)
  case class KeyboardEvent(event: dom.KeyboardEvent) extends TreeViewUserAction


If The user double clicks on the node, or presses the Enter key, the “NodeOpened” action is triggered. When he selects a node with keyboard or mouse, the NodeSelected action is triggered.

The tree is initialized with optional list of keys containing node + all ancestors, which will be expanded, loaded and selected at the beginning.

Create a test set of nested nodes, one of them remains unloaded, so its children attribute is None and create loadChilderenFn function which for given key returns children to be loaded and appended to the tree. Pass this function to the init function when creating the tree and create a simple Scala.js test application.
 */

package com.dalineage.client.treecomponent

import org.scalajs.dom
import org.scalajs.dom.document
import scala.concurrent.Future
import scala.concurrent.ExecutionContext.Implicits.global
import scala.util.{Success, Failure}
import cats.implicits._
import com.dalineage.common.adt.TreeNodeADT.TreeNode

// ─── USER ACTIONS ──────────────────────────────────────────────
sealed trait TreeViewUserAction
case class NodeSelected(node: TreeNode)                  extends TreeViewUserAction
case class NodeOpened(node: TreeNode, ctrlKey: Boolean)  extends TreeViewUserAction
case class KeyboardEvent(event: dom.KeyboardEvent)       extends TreeViewUserAction

// ─── TREE EXPLORER ─────────────────────────────────────────────
object TreeExplorer:

  // — runtime state ---------------------------------------------------------
  private var root: TreeNode                                = scala.compiletime.uninitialized
  private var selectedKey: String                           = scala.compiletime.uninitialized
  private var expanded: Set[String]                         = Set.empty
  private var fullyLoadedEmpty: Set[String]                 = Set.empty
  private var loadChildren: String => Future[Seq[TreeNode]] = scala.compiletime.uninitialized
  private var userAction:  TreeViewUserAction => Future[Unit] = scala.compiletime.uninitialized

  private def isLeaf(node: TreeNode): Boolean =
    node.children.exists(_.isEmpty)

  // ─── public API ----------------------------------------------------------
  /** Initialise the component.
    *
    * @param initialTree   already-available root (and any known children)
    * @param loadChildrenFn async loader, called when a node is first expanded
    * @param userActionFn  callback for UI events
    * @param initialPath   full list of keys from root to the node that should
    *                      start out **expanded & selected**; use `None`
    *                      to keep the old behaviour (root selected).
    */
  def init(
      initialTree: TreeNode,
      loadChildrenFn: String => Future[Seq[TreeNode]],
      userActionFn:  TreeViewUserAction => Future[Unit],
      initialPath: Option[List[String]] = None
  ): Unit =
    root         = initialTree
    loadChildren = loadChildrenFn
    userAction   = userActionFn

    // ensure the path starts with the root key
    val fullPath: List[String] = initialPath match
      case Some(path) if path.headOption.contains(initialTree.key) => path
      case Some(path)                                              => initialTree.key :: path
      case None                                                    => List(initialTree.key)

    selectedKey = fullPath.last
    expanded    = fullPath.toSet

    renderTree()

    val container = document
      .getElementById("tree-container")
      .asInstanceOf[dom.html.Element]
    container.addEventListener("keydown", (e: dom.KeyboardEvent) => handleKey(e), true)

    // load & expand every segment of the path in order
    loadAndExpand(fullPath)

    container.focus()

  // ─── ensure the whole path is present, expanding on the way ──────────────
  private def loadAndExpand(path: List[String]): Unit =
    path match
      case Nil => ()                                        // finished
      case key :: rest =>
        expanded += key
        findNodeByKey(root, key) match
          case None => ()                                   // should not happen
          case Some(node) =>
            node.children match
              case Some(_) =>                               // already loaded
                loadAndExpand(rest)
              case None =>                                  // fetch before going deeper
                loadChildren(node.key).onComplete {
                  case Success(children) =>
                    root = updateNode(root, node.key, children)
                    if children.isEmpty then fullyLoadedEmpty += node.key
                    renderTree()
                    loadAndExpand(rest)
                  case Failure(err) =>
                    println(s"Error loading children for ${node.key}: ${err.getMessage}")
                }

  // ─── rendering -----------------------------------------------------------
  private def renderTree(): Unit =
    val container = document
      .getElementById("tree-container")
      .asInstanceOf[dom.html.Element]
    container.innerHTML = ""
    container.appendChild(createNodeElement(root))
    ensureSelectedVisible()
    container.focus()

  /** Make sure the currently selected node is visible in the viewport. */
  private def ensureSelectedVisible(): Unit =
    val elem = document.querySelector(".node-header.selected")
    if elem != null then
      elem.asInstanceOf[dom.html.Element].scrollIntoView()

  private def getVisibleNodes(node: TreeNode): Seq[TreeNode] =
    val here = Seq(node)
    if expanded.contains(node.key) then
      node.children match
        case Some(ch) => here ++ ch.flatMap(getVisibleNodes)
        case None     => here
    else here

  // ─── keyboard handling ---------------------------------------------------
  private def handleKey(e: dom.KeyboardEvent): Unit =
    findNodeByKey(root, selectedKey).foreach { current =>
      e.key match
        case "ArrowRight" =>
          if !expanded.contains(current.key) && !isLeaf(current) then
            expandOrLoad(current)
          else
            current.children.flatMap(_.headOption).foreach(selectNode)
        case "ArrowLeft"  =>
          if expanded.contains(current.key) then
            expanded -= current.key
            renderTree()
          else
            current.parentKey.flatMap(findNodeByKey(root, _)).foreach(selectNode)
        case "ArrowUp"    => moveSelection(-1)
        case "ArrowDown"  => moveSelection(+1)
        case "Enter"      => userAction(NodeOpened(current, e.ctrlKey))
        case _            => userAction(KeyboardEvent(e))
    }

  private def moveSelection(delta: Int): Unit =
    val visible = getVisibleNodes(root)
    val idx     = visible.indexWhere(_.key == selectedKey) + delta
    if idx >= 0 && idx < visible.length then selectNode(visible(idx))

  private def selectNode(node: TreeNode): Unit =
    selectedKey = node.key
    userAction(NodeSelected(node))
    renderTree()

  // ─── data helpers --------------------------------------------------------
  private def updateNode(current: TreeNode, key: String, newChildren: Seq[TreeNode]): TreeNode =
    if current.key == key then current.copy(children = Some(newChildren))
    else current.children match
      case Some(ch) => current.copy(children = Some(ch.map(updateNode(_, key, newChildren))))
      case None     => current

  def fullPath(node: TreeNode): List[String] =
    node.parentKey match
      case Some(parentKey) =>
        val parent = findNodeByKey(root, parentKey)
        fullPath(parent.get) :+ java.net.URLEncoder.encode(node.key, "UTF-8")
      case None => Nil

  private def findNodeByKey(current: TreeNode, key: String): Option[TreeNode] =
    if current.key == key then Some(current)
    else current.children.flatMap(_.view.flatMap(findNodeByKey(_, key)).headOption)

  // ─── expand / load at run-time ------------------------------------------
  private def expandOrLoad(node: TreeNode): Unit =
    expanded += node.key

    val needsLoad =
      node.children match
        case None                                  => true
        case Some(seq) if seq.isEmpty && !fullyLoadedEmpty.contains(node.key) => true
        case _                                    => false

    if needsLoad then
      loadChildren(node.key).onComplete {
        case Success(children) =>
          root = updateNode(root, node.key, children)
          if children.isEmpty then
            fullyLoadedEmpty += node.key
            expanded          -= node.key
          renderTree()
        case Failure(err) =>
          println(s"Error loading children for ${node.key}: ${err.getMessage}")
      }
    else
      renderTree()

  // ─── DOM building --------------------------------------------------------
  private def createNodeElement(node: TreeNode): dom.html.Element =
    val container = document.createElement("div").asInstanceOf[dom.html.Element]
    container.classList.add("tree-node")

    val header = document.createElement("div").asInstanceOf[dom.html.Element]
    header.classList.add("node-header")
    if node.key == selectedKey then header.classList.add("selected")
    header.tabIndex = 0

    // toggle (+/–)
    if !isLeaf(node) then
      val button = document.createElement("span").asInstanceOf[dom.html.Element]
      button.classList.add("toggle-button")
      button.textContent = if expanded.contains(node.key) then "-" else "+"
      button.addEventListener("click", (e: dom.MouseEvent) =>
        e.stopPropagation()
        if expanded.contains(node.key) then
          expanded -= node.key
          renderTree()
        else expandOrLoad(node)
      )
      header.appendChild(button)
    else
      val spacer = document.createElement("span").asInstanceOf[dom.html.Element]
      spacer.classList.add("toggle-spacer")
      spacer.textContent = " "
      header.appendChild(spacer)

    // icon + caption
    val iconSpan = document.createElement("span").asInstanceOf[dom.html.Element]
    iconSpan.classList.add("icon")
    iconSpan.textContent = node.icon
    header.appendChild(iconSpan)

    val captionSpan = document.createElement("span").asInstanceOf[dom.html.Element]
    captionSpan.classList.add("caption")
    captionSpan.textContent = node.caption
    captionSpan.setAttribute("title", node.toolTip)
    header.appendChild(captionSpan)

    // header-level events
    header.addEventListener("click", (_: dom.MouseEvent) => selectNode(node))
    header.addEventListener("dblclick", (e: dom.MouseEvent) => userAction(NodeOpened(node, e.ctrlKey)))
    header.addEventListener("keydown", (e: dom.KeyboardEvent) =>
      if e.key == "Enter" then userAction(NodeOpened(node, e.ctrlKey)))

    container.appendChild(header)

    // children
    if expanded.contains(node.key) then
      node.children.foreach { ch =>
        if ch.nonEmpty then
          val childBox = document.createElement("div").asInstanceOf[dom.html.Element]
          childBox.classList.add("children-container")
          ch.foreach(c => childBox.appendChild(createNodeElement(c)))
          container.appendChild(childBox)
      }

    container
end TreeExplorer
